import { useMemo } from "react";

import { useGetHistory as useGetExternalHistory } from "@synthesis/shared-services/external/application/hooks";
import { useGetHistory as useGetInternalHistory } from "@synthesis/shared-services/internal/application/hooks";

import { useClientType } from "../../../shared/hooks/use-client-type";
import {
  buildApplicationHistoryFromEventLogs,
  isApplicationNgApprovalAfterNtpRework,
  isApplicationNtpApprovalAfterNtpRework,
  isApplicationReturnedAfterApplicantRevision,
  type ApplicationDetailHistoryEntry,
} from "../application-history.lib";

export const useApplicationHistory = (requestId: string) => {
  const clientType = useClientType();
  const shouldLoadExternal = clientType === "external" && Boolean(requestId);
  const shouldLoadInternal = clientType === "internal" && Boolean(requestId);

  const externalHistoryQuery = useGetExternalHistory(
    { path: { id: requestId } },
    {
      query: {
        enabled: shouldLoadExternal,
      },
    },
  );
  const internalHistoryQuery = useGetInternalHistory(
    { path: { id: requestId } },
    {
      query: {
        enabled: shouldLoadInternal,
      },
    },
  );

  const entries = useMemo((): ApplicationDetailHistoryEntry[] => {
    if (shouldLoadExternal && externalHistoryQuery.data) {
      return buildApplicationHistoryFromEventLogs(externalHistoryQuery.data);
    }

    if (shouldLoadInternal && internalHistoryQuery.data) {
      return buildApplicationHistoryFromEventLogs(internalHistoryQuery.data);
    }

    return [];
  }, [externalHistoryQuery.data, internalHistoryQuery.data, shouldLoadExternal, shouldLoadInternal]);

  const isLoading = (shouldLoadExternal && externalHistoryQuery.isLoading) || (shouldLoadInternal && internalHistoryQuery.isLoading);

  const isReturnedAfterApplicantRevision = useMemo(() => {
    if (shouldLoadInternal && internalHistoryQuery.data) {
      return isApplicationReturnedAfterApplicantRevision(internalHistoryQuery.data);
    }

    return false;
  }, [internalHistoryQuery.data, shouldLoadInternal]);

  const isNgApprovalAfterNtpRework = useMemo(() => {
    if (shouldLoadInternal && internalHistoryQuery.data) {
      return isApplicationNgApprovalAfterNtpRework(internalHistoryQuery.data);
    }

    return false;
  }, [internalHistoryQuery.data, shouldLoadInternal]);

  const isNtpApprovalAfterNtpRework = useMemo(() => {
    if (shouldLoadInternal && internalHistoryQuery.data) {
      return isApplicationNtpApprovalAfterNtpRework(internalHistoryQuery.data);
    }

    return false;
  }, [internalHistoryQuery.data, shouldLoadInternal]);

  return {
    entries,
    isLoading,
    isReturnedAfterApplicantRevision,
    isNgApprovalAfterNtpRework,
    isNtpApprovalAfterNtpRework,
    isError: (shouldLoadExternal && externalHistoryQuery.isError) || (shouldLoadInternal && internalHistoryQuery.isError),
  };
};
